import { useTranslation } from "react-i18next";

import { cn } from "~/core/lib/utils";

import {
  type CellTone,
  DAY_ORDER,
  type ScheduleTable,
} from "../lib/schedule-data";

function toneClass(tone: CellTone): string {
  switch (tone) {
    case "class":
      return "bg-card text-foreground";
    case "highlight":
      return "bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-100";
    case "closed":
      return "bg-rose-100 text-rose-900 dark:bg-rose-950/50 dark:text-rose-100";
    case "empty":
    default:
      return "bg-card";
  }
}

export function ScheduleTable({ schedule }: { schedule: ScheduleTable }) {
  const { t } = useTranslation();

  return (
    <>
      <div className="overflow-x-auto rounded-xl border shadow-sm">
        <table className="w-full min-w-[640px] border-separate border-spacing-0 text-center text-sm sm:text-base">
          <thead>
            <tr>
              <th
                scope="col"
                className="bg-muted w-[22%] min-w-[9rem] border-b p-3 align-middle font-medium"
                aria-hidden
              />
              {DAY_ORDER.map((day) => (
                <th
                  key={day}
                  scope="col"
                  className="bg-muted border-b p-3 align-middle font-medium"
                >
                  {t(`programsPage.day.${day}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {schedule.rows.map((row, ri) => (
              <tr key={ri}>
                <th
                  scope="row"
                  className="bg-muted/40 text-muted-foreground border-t border-r p-3 align-middle text-xs font-medium sm:text-sm"
                >
                  <TimeRangeText value={row.time} />
                </th>
                {DAY_ORDER.map((day) => {
                  const cell = row.cells[day];
                  if (cell.skip) return null;
                  const rowSpan = cell.rowSpan ?? 1;
                  const isTallMerge = rowSpan > 1;
                  return (
                    <td
                      key={day}
                      rowSpan={rowSpan}
                      className={cn(
                        "border-t border-r p-3 align-middle last:border-r-0",
                        toneClass(cell.tone),
                      )}
                    >
                      <CellContent
                        subLabel={cell.subLabel}
                        labelKeys={cell.labelKeys}
                        tall={isTallMerge}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ScheduleLegend />
    </>
  );
}

interface CellContentProps {
  subLabel?: string;
  labelKeys?: string[];
  tall: boolean;
}

function CellContent({ subLabel, labelKeys, tall }: CellContentProps) {
  const { t } = useTranslation();

  if (!subLabel && (!labelKeys || labelKeys.length === 0)) {
    return <span aria-hidden>&nbsp;</span>;
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center",
        tall ? "min-h-[13rem] gap-4 sm:min-h-[15rem]" : "min-h-[2.5rem] gap-1",
      )}
    >
      {subLabel ? (
        <div className="text-xs font-semibold whitespace-nowrap sm:text-sm">
          <TimeRangeText value={subLabel} />
        </div>
      ) : null}
      {labelKeys && labelKeys.length > 0 ? (
        <div
          className={cn(
            "flex flex-col items-center",
            tall ? "gap-3 text-base sm:text-lg" : "text-sm sm:text-base",
          )}
        >
          {labelKeys.map((key, i) => (
            <span key={i}>
              {t(`programsPage.label.${key}`, { defaultValue: key })}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TimeRangeText({ value }: { value: string }) {
  if (!value) return <span aria-hidden>&nbsp;</span>;

  const parts = value.split(/\s+[–-]\s+/);
  if (parts.length !== 2) return <span>{value}</span>;

  const [start, end] = parts;

  return (
    <span className="inline-flex flex-col items-center leading-tight">
      <span>{start} –</span>
      <span>{end}</span>
    </span>
  );
}

function ScheduleLegend() {
  const { t } = useTranslation();
  return (
    <ul className="text-muted-foreground mt-4 flex flex-wrap items-center gap-4 text-xs sm:text-sm">
      <li className="flex items-center gap-2">
        <span
          aria-hidden
          className="inline-block size-3 rounded-sm border bg-sky-100 dark:bg-sky-950/60"
        />
        {t("programsPage.legendSparring")}
      </li>
      <li className="flex items-center gap-2">
        <span
          aria-hidden
          className="inline-block size-3 rounded-sm border bg-rose-100 dark:bg-rose-950/50"
        />
        {t("programsPage.legendClosed")}
      </li>
    </ul>
  );
}
